import React, { useEffect, useState, useCallback } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Loader2, ExternalLink, Globe2, Flag, Anchor, Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";

export default function AuleloWorld() {
  const [tab, setTab] = useState("india");
  const [data, setData] = useState({ items: [] });
  const [busy, setBusy] = useState(false);
  const [gleaning, setGleaning] = useState(null);
  const [feedback, setFeedback] = useState({});

  const loadFeedback = useCallback(async () => {
    try { const { data } = await auleloApi.get("/news/feedback"); setFeedback(data || {}); } catch {}
  }, []);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const load = async (which) => {
    setBusy(true);
    try {
      const path = { india: "/news/india", world: "/news/international", china: "/news/china", bilateral: "/news/bilateral" }[which];
      const { data } = await auleloApi.get(path);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
      setData({ items: [] });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const handleFeedback = async (it, next) => {
    const url = it.url;
    if (!url) return;
    const curr = feedback[url];
    const signal = curr === next ? "clear" : next;
    try {
      await auleloApi.post("/news/feedback", { url, signal, title: it.title, source: it.source });
      setFeedback((f) => {
        const copy = { ...f };
        if (signal === "clear") delete copy[url]; else copy[url] = signal;
        return copy;
      });
    } catch { toast.error("Couldn't save"); }
  };

  const glean = async (it) => {
    setGleaning(it.url);
    try {
      const { data } = await auleloApi.post("/vasa/glean", {
        title: it.title,
        body: (it.snippet || "") + "\n\n(source: " + (it.source || "") + ")",
        url: it.url,
        max_claims: 4,
      });
      toast.success(`Saved ${data.saved} fact-checked claim(s) to Vasa · Chosen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Glean failed");
    } finally {
      setGleaning(null);
    }
  };

  const tabs = [
    { id: "india",      label: "India domestic",  icon: Flag },
    { id: "world",      label: "International",   icon: Globe2 },
    { id: "china",      label: "China watch",     icon: Anchor },
    { id: "bilateral",  label: "Bilateral",       icon: Globe2 },
  ];

  return (
    <div className="space-y-6" data-testid="aulelo-world">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// raw signal</div>
        <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Wires &amp; cables.</h1>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-emerald-900/40 pb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`aulelo-world-tab-${t.id}`}
            className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border flex items-center gap-2 ${
              tab === t.id ? "border-emerald-500 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 text-zinc-500 hover:text-emerald-300"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {busy && (<div className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>)}

      {!busy && (data.items || []).length === 0 && (
        <div className="border border-dashed border-zinc-800 p-10 text-center text-zinc-500 font-mono text-sm">
          No items returned. Adjust filters under <b className="text-emerald-300">Vault</b>.
        </div>
      )}

      {!busy && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.items || []).map((it, i) => (
            <div
              key={i}
              data-testid={`aulelo-news-item-${i}`}
              className="border border-zinc-800 bg-black p-4 hover:border-emerald-700/50 transition group flex flex-col"
            >
              <a href={it.url} target="_blank" rel="noreferrer" className="flex-1 block">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500">{it.source}</div>
                  <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-emerald-300" />
                </div>
                <h3 className="font-mono text-[15px] font-bold text-emerald-100 leading-snug line-clamp-3">{it.title}</h3>
                <p className="text-zinc-400 text-[13px] mt-2 line-clamp-3">{it.snippet}</p>
              </a>
              <button
                onClick={() => glean(it)}
                disabled={gleaning === it.url}
                data-testid={`aulelo-glean-btn-${i}`}
                className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] border border-amber-700/40 text-amber-300 hover:bg-amber-500/10 py-2 flex items-center justify-center gap-2"
              >
                {gleaning === it.url ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {gleaning === it.url ? "gleaning…" : "Glean & cross-check"}
              </button>
              <div className="mt-2 flex gap-1" data-testid={`aulelo-news-fb-${i}`}>
                <button
                  onClick={() => handleFeedback(it, "like")}
                  data-testid={`aulelo-news-like-${i}`}
                  aria-label="Useful"
                  className={`flex-1 p-1.5 border ${feedback[it.url] === "like" ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-emerald-700/40"}`}
                >
                  <ThumbsUp className="w-3 h-3 mx-auto" />
                </button>
                <button
                  onClick={() => handleFeedback(it, "dislike")}
                  data-testid={`aulelo-news-dislike-${i}`}
                  aria-label="Not useful"
                  className={`flex-1 p-1.5 border ${feedback[it.url] === "dislike" ? "border-red-500 bg-red-500/15 text-red-300" : "border-zinc-800 text-zinc-500 hover:text-red-300 hover:border-red-700/40"}`}
                >
                  <ThumbsDown className="w-3 h-3 mx-auto" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
